import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
    FaWhatsapp, FaTimes, FaCheckCircle, FaShieldAlt,
    FaLock, FaMapMarkerAlt, FaFileInvoice, FaArrowRight,
    FaCopy, FaCheck, FaEye, FaEyeSlash, FaKey
} from "react-icons/fa";
import { toast } from "react-hot-toast";

const PurchaseModal = ({ product, initialQuantity = 1, onClose }) => {
    const { user, isAuthenticated, setSession } = useAuth();
    const navigate = useNavigate();

    const [copiedAll, setCopiedAll] = useState(false);
    const [copiedField, setCopiedField] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showSuccessPassword, setShowSuccessPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submittedData, setSubmittedData] = useState(null);

    const [form, setForm] = useState({
        productId: product?._id || "",
        productName: product?.name || "LUMIGO Commercial Gobo Projector",
        modelNumber: product?.modelNumber || "",
        quantity: Math.max(1, initialQuantity),
        customerName: user?.name || "",
        email: user?.email || "",
        password: "",
        confirmPassword: "",
        phone: user?.phone || "",
        companyName: user?.companyName || "",
        address: user?.address || "",
        city: user?.city || "",
        state: user?.state || "",
        pincode: user?.pincode || "",
        requirements: "",
        preferredContact: "WhatsApp",
        termsAccepted: true,
    });

    const unitPrice = product?.discountPrice || product?.price || 0;
    const subtotal = unitPrice * form.quantity;

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleQuantityChange = (delta) => {
        setForm(prev => ({
            ...prev,
            quantity: Math.max(1, prev.quantity + delta),
        }));
    };

    const handleCopyField = (text, fieldName) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedField(fieldName);
        toast.success(`${fieldName} copied to clipboard!`);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const handleCopyAllCredentials = (creds, refNum) => {
        const text = `LUMIGO COMMERCIAL CLIENT ACCOUNT\nOrder Reference: ${refNum}\nLogin Portal: ${window.location.origin}/login\nEmail / Username: ${creds.email}\nPassword: ${creds.password}\nCustomer Support / WhatsApp: +91 8999427831`;
        navigator.clipboard.writeText(text);
        setCopiedAll(true);
        toast.success("All Account Credentials copied to clipboard!");
        setTimeout(() => setCopiedAll(false), 2500);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return; // Prevent double click

        if (!form.customerName || !form.email || !form.phone) {
            toast.error("Please enter your name, email, and contact number.");
            return;
        }

        if (!isAuthenticated) {
            if (!form.password || form.password.length < 6) {
                toast.error("Please set a password of at least 6 characters for your client account.");
                return;
            }
            if (form.password !== form.confirmPassword) {
                toast.error("Password and Confirm Password do not match.");
                return;
            }
        }

        if (!form.termsAccepted) {
            toast.error("Please accept the Terms & Conditions and Warranty Policy to proceed.");
            return;
        }

        setSubmitting(true);
        try {
            const { data } = await api.post("/api/enquiries/purchase-register", form);

            // Immediately set active session so user is logged in
            if (data.token && data.user && setSession) {
                setSession(data.token, data.user);
            }

            setSubmittedData(data);
            toast.success(data.message || "Order registered successfully!");
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to submit request. Please try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    // SUCCESS CONFIRMATION MODAL VIEW
    if (submittedData) {
        const enquiry = submittedData.enquiry;
        const refNumber = submittedData.enquiryRef || enquiry?.enquiryRef;
        const creds = {
            email: submittedData.user?.email || enquiry?.email || form.email,
            password: form.password || "Provided during registration",
        };

        return (
            <div className="fixed inset-0 bg-black/75 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 z-50 animate-fadeIn">
                <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-3xl p-6 sm:p-8 max-w-lg w-full text-center shadow-2xl relative max-h-[92vh] overflow-y-auto">
                    <button
                        onClick={onClose}
                        className="absolute top-5 right-5 w-8 h-8 rounded-full bg-[#F7F5EF] text-[#687482] hover:text-[#263746] flex items-center justify-center transition"
                    >
                        <FaTimes />
                    </button>

                    <div className="w-16 h-16 bg-[#FFF7E0] text-[#E3B629] rounded-2xl flex items-center justify-center mx-auto mb-4 text-3xl border border-[#E3B629]/40 shadow-xs">
                        <FaCheckCircle />
                    </div>

                    <span className="text-[#E3B629] text-[10px] font-black uppercase tracking-[0.25em] block mb-1">
                        Order & Client Registration Confirmed
                    </span>
                    <h2 className="text-2xl font-black text-[#263746] tracking-tight mb-2">
                        Request Received
                    </h2>

                    {/* Reference Card with Copy */}
                    <div className="bg-[#F7F5EF] border border-[#E5E7EB] rounded-2xl p-4 my-3 flex items-center justify-between">
                        <div className="text-left">
                            <span className="text-[10px] text-[#687482] uppercase font-bold tracking-wider block">
                                Order Reference Number
                            </span>
                            <span className="text-lg font-mono font-black text-[#263746]">
                                {refNumber}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => handleCopyField(refNumber, "Reference Number")}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-[#E5E7EB] text-xs font-bold text-[#263746] hover:bg-[#E5E7EB] transition cursor-pointer"
                        >
                            {copiedField === "Reference Number" ? <FaCheck className="text-[#168B53]" /> : <FaCopy />}
                            <span>{copiedField === "Reference Number" ? "Copied" : "Copy"}</span>
                        </button>
                    </div>

                    {/* DEDICATED CLIENT CREDENTIALS MODULE */}
                    <div className="bg-amber-50/70 border-2 border-[#E3B629]/50 rounded-2xl p-4 text-left my-4 shadow-xs">
                        <div className="flex items-center gap-2 mb-2">
                            <FaKey className="text-[#E3B629] text-sm" />
                            <h4 className="text-xs font-black uppercase tracking-wider text-[#263746]">
                                Your Commercial Client Dashboard Credentials
                            </h4>
                        </div>
                        <p className="text-[11px] text-[#687482] mb-3 leading-relaxed">
                            Save these credentials to log in to your dashboard anytime to track quotation drawings, dispatch dates, and order status:
                        </p>

                        <div className="space-y-2 text-xs">
                            {/* Email / Username */}
                            <div className="bg-white rounded-xl p-2.5 border border-[#E5E7EB] flex items-center justify-between">
                                <div>
                                    <span className="text-[10px] uppercase font-bold text-[#687482] block">Username / Login Email</span>
                                    <span className="font-mono font-bold text-[#263746]">{creds.email}</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleCopyField(creds.email, "Username")}
                                    className="p-1.5 rounded bg-[#F7F5EF] hover:bg-[#E5E7EB] text-[#263746] transition text-xs"
                                    title="Copy Username"
                                >
                                    {copiedField === "Username" ? <FaCheck className="text-[#168B53]" /> : <FaCopy />}
                                </button>
                            </div>

                            {/* Password */}
                            <div className="bg-white rounded-xl p-2.5 border border-[#E5E7EB] flex items-center justify-between">
                                <div>
                                    <span className="text-[10px] uppercase font-bold text-[#687482] block">Dashboard Password</span>
                                    <span className="font-mono font-bold text-[#263746]">
                                        {showSuccessPassword ? creds.password : "••••••••••••"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => setShowSuccessPassword(!showSuccessPassword)}
                                        className="p-1.5 rounded bg-[#F7F5EF] hover:bg-[#E5E7EB] text-[#263746] transition text-xs"
                                        title={showSuccessPassword ? "Hide password" : "Show password"}
                                    >
                                        {showSuccessPassword ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleCopyField(creds.password, "Password")}
                                        className="p-1.5 rounded bg-[#F7F5EF] hover:bg-[#E5E7EB] text-[#263746] transition text-xs"
                                        title="Copy Password"
                                    >
                                        {copiedField === "Password" ? <FaCheck className="text-[#168B53]" /> : <FaCopy />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* One-Click Copy All */}
                        <button
                            type="button"
                            onClick={() => handleCopyAllCredentials(creds, refNumber)}
                            className="mt-3 w-full bg-[#FFFFFF] hover:bg-[#F7F5EF] border border-[#E3B629] text-[#263746] font-bold py-2 rounded-xl text-xs flex items-center justify-center gap-1.5 transition cursor-pointer"
                        >
                            {copiedAll ? <FaCheck className="text-[#168B53]" /> : <FaCopy className="text-[#E3B629]" />}
                            <span>{copiedAll ? "Credentials Saved to Clipboard!" : "Copy All Credentials to Save"}</span>
                        </button>
                    </div>

                    {/* Dual Action Buttons */}
                    <div className="space-y-2.5">
                        <button
                            type="button"
                            onClick={() => {
                                onClose();
                                navigate("/customer");
                            }}
                            className="w-full bg-[#263746] hover:bg-[#1b2732] text-white font-extrabold text-xs py-3.5 px-6 rounded-xl flex items-center justify-center gap-2 uppercase tracking-wider transition shadow-sm cursor-pointer"
                        >
                            <FaFileInvoice className="text-sm text-[#E3B629]" />
                            <span>Go to My Client Dashboard</span>
                            <FaArrowRight className="text-[10px]" />
                        </button>

                        {submittedData.whatsappUrl && (
                            <a
                                href={submittedData.whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full bg-[#168B53] hover:bg-[#137446] text-white font-bold text-xs py-3.5 px-6 rounded-xl flex items-center justify-center gap-2 uppercase tracking-wider transition shadow-sm"
                            >
                                <FaWhatsapp className="text-base" />
                                <span>Continue on WhatsApp (+91 8999427831)</span>
                            </a>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // MAIN ORDER & CLIENT REGISTRATION FORM VIEW
    return (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 z-50 animate-fadeIn">
            <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-3xl max-w-2xl w-full shadow-2xl relative max-h-[92vh] flex flex-col overflow-hidden">

                {/* Modal Top Bar */}
                <div className="p-5 sm:p-6 bg-[#F7F5EF] border-b border-[#E5E7EB] flex items-center justify-between flex-shrink-0">
                    <div>
                        <span className="text-[#E3B629] text-[10px] font-black uppercase tracking-[0.25em] block">
                            Direct Purchase & Instant Client Setup
                        </span>
                        <h2 className="text-lg sm:text-xl font-extrabold text-[#263746] tracking-tight">
                            Order {product?.modelNumber || "Gobo Projector"}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full bg-white border border-[#E5E7EB] text-[#687482] hover:text-[#263746] flex items-center justify-center transition"
                        aria-label="Close modal"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Form Body (Scrollable) */}
                <form onSubmit={handleSubmit} className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-4 text-xs text-[#263746]">

                    {/* 1. Selected Product Banner */}
                    <div className="flex items-center gap-4 p-3.5 rounded-2xl bg-[#F7F5EF] border border-[#E5E7EB]">
                        <img
                            src={product?.images?.[0] || product?.image || "/assets/lumigo/projector-1.jpg"}
                            alt={form.productName}
                            className="w-16 h-16 object-contain rounded-xl bg-white border border-[#E5E7EB] p-1 flex-shrink-0"
                            onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                        />
                        <div className="flex-1 min-w-0">
                            <h3 className="font-extrabold text-sm text-[#263746] truncate">
                                {form.productName}
                            </h3>
                            <div className="text-[11px] text-[#687482] flex flex-wrap items-center gap-2 mt-0.5">
                                <span>Model: <strong className="text-[#263746] font-mono">{form.modelNumber || "DXAX-SERIES"}</strong></span>
                                {unitPrice > 0 && (
                                    <span>• Unit: <strong className="text-[#263746] font-mono">₹{unitPrice.toLocaleString()}</strong></span>
                                )}
                                {subtotal > 0 && form.quantity > 1 && (
                                    <span>• Total: <strong className="text-[#168B53] font-mono">₹{subtotal.toLocaleString()}</strong></span>
                                )}
                            </div>
                        </div>

                        {/* Quantity Incrementer */}
                        <div className="flex items-center border border-[#E5E7EB] rounded-lg bg-white overflow-hidden flex-shrink-0">
                            <button
                                type="button"
                                onClick={() => handleQuantityChange(-1)}
                                className="px-2.5 py-1.5 text-xs text-[#263746] hover:bg-[#F7F5EF] font-bold"
                            >
                                -
                            </button>
                            <span className="px-2 text-xs font-bold text-[#263746] min-w-[24px] text-center">
                                {form.quantity}
                            </span>
                            <button
                                type="button"
                                onClick={() => handleQuantityChange(1)}
                                className="px-2.5 py-1.5 text-xs text-[#263746] hover:bg-[#F7F5EF] font-bold"
                            >
                                +
                            </button>
                        </div>
                    </div>

                    {/* 2. Customer Credentials / Account Creation Info */}
                    <div className="pt-1">
                        <span className="font-extrabold text-xs text-[#263746] uppercase tracking-wider block mb-2.5">
                            1. Client Identity & Account Login Credentials
                        </span>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[#687482] font-semibold mb-1">
                                    Full Name <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="customerName"
                                    value={form.customerName}
                                    onChange={onChange}
                                    placeholder="Your Name"
                                    required
                                    className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-xl px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] focus:bg-white"
                                />
                            </div>

                            <div>
                                <label className="block text-[#687482] font-semibold mb-1">
                                    Phone / WhatsApp Number <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={form.phone}
                                    onChange={onChange}
                                    placeholder="10-digit mobile number"
                                    required
                                    className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-xl px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] focus:bg-white"
                                />
                            </div>

                            <div>
                                <label className="block text-[#687482] font-semibold mb-1">
                                    Email Address (Your Client Login ID) <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={onChange}
                                    placeholder="name@company.com"
                                    required
                                    className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-xl px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] focus:bg-white"
                                />
                            </div>

                            {!isAuthenticated ? (
                                <>
                                    <div>
                                        <label className="block text-[#687482] font-semibold mb-1 flex items-center justify-between">
                                            <span>Account Password <span className="text-rose-500">*</span></span>
                                            <span className="text-[10px] text-[#E3B629] font-bold">Auto-Creates Account</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                value={form.password}
                                                onChange={onChange}
                                                placeholder="Choose a password (min 6 chars)"
                                                required={!isAuthenticated}
                                                minLength={6}
                                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-xl pl-9 pr-9 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] focus:bg-white"
                                            />
                                            <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#687482] text-xs" />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#687482] hover:text-[#263746] text-xs p-1"
                                                title={showPassword ? "Hide password" : "Show password"}
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-[#687482] font-semibold mb-1 flex items-center justify-between">
                                            <span>Confirm Password <span className="text-rose-500">*</span></span>
                                            <span className="text-[10px] text-[#687482]">Verify password</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="confirmPassword"
                                                value={form.confirmPassword}
                                                onChange={onChange}
                                                placeholder="Re-type your password"
                                                required={!isAuthenticated}
                                                minLength={6}
                                                className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-xl pl-9 pr-9 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] focus:bg-white"
                                            />
                                            <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#687482] text-xs" />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#687482] hover:text-[#263746] text-xs p-1"
                                                title={showConfirmPassword ? "Hide password" : "Show password"}
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div>
                                    <label className="block text-[#687482] font-semibold mb-1">
                                        Company Name (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        name="companyName"
                                        value={form.companyName}
                                        onChange={onChange}
                                        placeholder="Company / Organisation"
                                        className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-xl px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] focus:bg-white"
                                    />
                                </div>
                            )}
                        </div>

                        {!isAuthenticated && (
                            <div className="mt-2.5">
                                <label className="block text-[#687482] font-semibold mb-1">
                                    Company / Organisation Name (Optional)
                                </label>
                                <input
                                    type="text"
                                    name="companyName"
                                    value={form.companyName}
                                    onChange={onChange}
                                    placeholder="Company / Facility Name"
                                    className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-xl px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] focus:bg-white"
                                />
                            </div>
                        )}
                    </div>

                    {/* 3. Delivery Location Details */}
                    <div className="pt-2">
                        <span className="font-extrabold text-xs text-[#263746] uppercase tracking-wider block mb-2.5 flex items-center gap-1.5">
                            <FaMapMarkerAlt className="text-[#E3B629]" />
                            <span>2. Delivery Location & Dispatch Address</span>
                        </span>

                        <div className="space-y-3">
                            <div>
                                <input
                                    type="text"
                                    name="address"
                                    value={form.address}
                                    onChange={onChange}
                                    placeholder="Street Address, Building, Floor, Industrial Area"
                                    required
                                    className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-xl px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] focus:bg-white"
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-2.5">
                                <div>
                                    <input
                                        type="text"
                                        name="city"
                                        value={form.city}
                                        onChange={onChange}
                                        placeholder="City (e.g. Pune)"
                                        required
                                        className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-xl px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] focus:bg-white"
                                    />
                                </div>
                                <div>
                                    <input
                                        type="text"
                                        name="state"
                                        value={form.state}
                                        onChange={onChange}
                                        placeholder="State"
                                        required
                                        className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-xl px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] focus:bg-white"
                                    />
                                </div>
                                <div>
                                    <input
                                        type="text"
                                        name="pincode"
                                        value={form.pincode}
                                        onChange={onChange}
                                        placeholder="Pincode"
                                        required
                                        className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-xl px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] focus:bg-white"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 4. Requirements & Optical Notes */}
                    <div className="pt-2">
                        <label className="block text-[#687482] font-semibold mb-1">
                            Projection Requirements / Throw Distance / Custom Logo Details
                        </label>
                        <textarea
                            name="requirements"
                            value={form.requirements}
                            onChange={onChange}
                            rows={2}
                            placeholder="e.g. Approximate projection distance (e.g. 10m), indoor vs outdoor, static or rotating gobo, or specific safety symbol needed."
                            className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-xl px-3.5 py-2 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] focus:bg-white resize-none"
                        />
                    </div>

                    {/* 5. Terms & Warranty Acceptance Checkbox */}
                    <div className="pt-2 border-t border-[#E5E7EB]">
                        <label className="flex items-start gap-2.5 cursor-pointer">
                            <input
                                type="checkbox"
                                name="termsAccepted"
                                checked={form.termsAccepted}
                                onChange={onChange}
                                required
                                className="mt-0.5 rounded border-[#E5E7EB] text-[#E3B629] focus:ring-[#E3B629]"
                            />
                            <span className="text-[11px] text-[#687482] leading-relaxed">
                                I accept the{" "}
                                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#263746] underline font-bold">
                                    Terms & Conditions
                                </a>{" "}
                                and{" "}
                                <a href="/warranty" target="_blank" rel="noopener noreferrer" className="text-[#263746] underline font-bold">
                                    1-Year Hardware Warranty Policy
                                </a>{" "}
                                of Fourlifters Private Limited.
                            </span>
                        </label>
                    </div>

                    {/* Submit Button Row */}
                    <div className="pt-3">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-black text-xs sm:text-sm py-4 px-6 rounded-xl flex items-center justify-center gap-2 uppercase tracking-wider transition shadow-md disabled:opacity-50 cursor-pointer"
                        >
                            {submitting ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-[#263746] border-t-transparent rounded-full animate-spin"></span>
                                    <span>Registering Order & Account...</span>
                                </>
                            ) : (
                                <>
                                    <span>Confirm Order & Create Account</span>
                                    <FaArrowRight className="text-xs" />
                                </>
                            )}
                        </button>

                        <p className="text-[11px] text-[#687482] text-center mt-2 flex items-center justify-center gap-1.5">
                            <FaShieldAlt className="text-[#168B53]" />
                            <span>Instant email confirmation and live shipment tracking in Client Dashboard</span>
                        </p>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default PurchaseModal;
